import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, ArrowLeft, Check } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface RequestCallModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export const RequestCallModal: React.FC<RequestCallModalProps> = ({ isOpen, onClose }) => {
    const [step, setStep] = useState(1);
    const [selectedService, setSelectedService] = useState('');
    const [projectDetails, setProjectDetails] = useState('');
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const services = [
        'New Website Creation',
        'Website Redesign',
        'Professional Catalogue',
        'Digital Banners'
    ];

    const handleClose = () => {
        onClose();
        setTimeout(() => {
            setStep(1);
            setSelectedService('');
            setProjectDetails('');
            setName('');
            setEmail('');
            setPhone('');
        }, 300);
    };

    const handleNext = () => {
        if (step < 4) setStep(step + 1);
    };

    const handleBack = () => {
        if (step > 1) setStep(step - 1);
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);

        try {
            const { error } = await supabase
                .from('zoom_call_requests')
                .insert([{
                    service: selectedService,
                    project_details: projectDetails || null,
                    name: name,
                    email: email,
                    phone: phone
                }]);

            if (error) {
                console.error('Supabase error:', error);
                alert('Failed to submit. Please try again.');
            } else {
                setStep(4); // Move to success step
            }
        } catch (err) {
            console.error('Unexpected error:', err);
            alert('Failed to submit. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-neutral-900 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-neutral-800 shadow-2xl"
            >
                {/* Header */}
                <div className="sticky top-0 bg-neutral-900 border-b border-neutral-800 px-8 py-6 flex items-center justify-between">
                    <div>
                        <h2 className="text-2xl font-bold text-white">Request Zoom Call</h2>
                        <p className="text-sm text-neutral-400 mt-1">Step {step} of 4</p>
                    </div>
                    <button
                        onClick={handleClose}
                        className="p-2 hover:bg-neutral-800 rounded-lg transition-colors"
                    >
                        <X className="text-neutral-400" size={24} />
                    </button>
                </div>

                {/* Content */}
                <div className="p-8">
                    <AnimatePresence mode="wait">
                        {step === 1 && (
                            <motion.div
                                key="step1"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                            >
                                <h3 className="text-xl font-semibold text-white mb-4">Select a Service</h3>
                                <p className="text-neutral-400 mb-6">What type of project are you interested in?</p>

                                <div className="space-y-3">
                                    {services.map((service) => (
                                        <button
                                            key={service}
                                            onClick={() => setSelectedService(service)}
                                            className={`w-full p-4 rounded-xl border-2 text-left transition-all ${selectedService === service
                                                ? 'border-purple-500 bg-purple-500/10'
                                                : 'border-neutral-800 hover:border-neutral-700 bg-neutral-800/50'
                                                }`}
                                        >
                                            <span className="text-white font-medium">{service}</span>
                                        </button>
                                    ))}
                                </div>

                                <button
                                    onClick={handleNext}
                                    disabled={!selectedService}
                                    className="mt-8 w-full py-3 bg-gradient-to-r from-purple-600 to-purple-500 text-white font-semibold rounded-full disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-purple-500/25 transition-all"
                                >
                                    Continue
                                    <ArrowRight size={18} />
                                </button>
                            </motion.div>
                        )}

                        {step === 2 && (
                            <motion.div
                                key="step2"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                            >
                                <h3 className="text-xl font-semibold text-white mb-4">Project Details (Optional)</h3>
                                <p className="text-neutral-400 mb-6">Tell us more about your project</p>

                                <textarea
                                    value={projectDetails}
                                    onChange={(e) => setProjectDetails(e.target.value)}
                                    placeholder="Describe your project, goals, timeline, or any specific requirements..."
                                    className="w-full h-40 p-4 bg-neutral-800 border border-neutral-700 rounded-xl text-white placeholder-neutral-500 focus:outline-none focus:border-purple-500 resize-none"
                                />

                                <div className="flex gap-3 mt-8">
                                    <button
                                        onClick={handleBack}
                                        className="flex-1 py-3 border border-neutral-700 text-white font-semibold rounded-full hover:bg-neutral-800 transition-colors flex items-center justify-center gap-2"
                                    >
                                        <ArrowLeft size={18} />
                                        Back
                                    </button>
                                    <button
                                        onClick={handleNext}
                                        className="flex-1 py-3 bg-gradient-to-r from-purple-600 to-purple-500 text-white font-semibold rounded-full hover:shadow-lg hover:shadow-purple-500/25 transition-all flex items-center justify-center gap-2"
                                    >
                                        Continue
                                        <ArrowRight size={18} />
                                    </button>
                                </div>
                            </motion.div>
                        )}

                        {step === 3 && (
                            <motion.div
                                key="step3"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                            >
                                <h3 className="text-xl font-semibold text-white mb-4">Your Contact Information</h3>
                                <p className="text-neutral-400 mb-6">We'll send you a Zoom link via email</p>

                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-neutral-300 mb-2">Full Name</label>
                                        <input
                                            type="text"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            placeholder="John Doe"
                                            className="w-full p-4 bg-neutral-800 border border-neutral-700 rounded-xl text-white placeholder-neutral-500 focus:outline-none focus:border-purple-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-neutral-300 mb-2">Email Address</label>
                                        <input
                                            type="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder="john@example.com"
                                            className="w-full p-4 bg-neutral-800 border border-neutral-700 rounded-xl text-white placeholder-neutral-500 focus:outline-none focus:border-purple-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-neutral-300 mb-2">Phone Number</label>
                                        <input
                                            type="tel"
                                            value={phone}
                                            onChange={(e) => setPhone(e.target.value)}
                                            placeholder="+91 98765 43210"
                                            className="w-full p-4 bg-neutral-800 border border-neutral-700 rounded-xl text-white placeholder-neutral-500 focus:outline-none focus:border-purple-500"
                                        />
                                    </div>
                                </div>

                                <div className="flex gap-3 mt-8">
                                    <button
                                        onClick={handleBack}
                                        className="flex-1 py-3 border border-neutral-700 text-white font-semibold rounded-full hover:bg-neutral-800 transition-colors flex items-center justify-center gap-2"
                                    >
                                        <ArrowLeft size={18} />
                                        Back
                                    </button>
                                    <button
                                        onClick={handleSubmit}
                                        disabled={!name || !email || !phone}
                                        className="flex-1 py-3 bg-gradient-to-r from-purple-600 to-purple-500 text-white font-semibold rounded-full disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg hover:shadow-purple-500/25 transition-all"
                                    >
                                        Submit Request
                                    </button>
                                </div>
                            </motion.div>
                        )}

                        {step === 4 && (
                            <motion.div
                                key="step4"
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="text-center py-8"
                            >
                                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <Check className="text-green-500" size={32} />
                                </div>
                                <h3 className="text-2xl font-bold text-white mb-4">Request Submitted!</h3>
                                <p className="text-neutral-400 mb-8">
                                    Thank you! We'll send you a Zoom link via email shortly to discuss your <span className="text-purple-400">{selectedService}</span> project.
                                </p>
                                <button
                                    onClick={handleClose}
                                    className="px-8 py-3 bg-gradient-to-r from-purple-600 to-purple-500 text-white font-semibold rounded-full hover:shadow-lg hover:shadow-purple-500/25 transition-all"
                                >
                                    Done
                                </button>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </motion.div>
        </div>
    );
};
